import type { FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

import type { YesNoBooleanFormValue } from '@customTypes/form';
import { isAdultDOB } from '@utils/dates';
import { EmailValidator } from '@validators/email';
import { NullableStringValidator } from '@validators/strings';
import { AdultDOBValidator, DOBValidator } from '@validators/subject';

export interface OrderingGuardianFormValues {
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  phone: string;
  relationship: string;
  legalAuthorityConfirmed: boolean;
}

export interface OrderingEligibilityValidatedFormValues {
  firstName: string;
  lastName: string;
  dob: string;
  sexAtBirth: string;
  email: string | null;
  phone: string | null;
  boneMarrowTransplant: YesNoBooleanFormValue;
  promoCode: string | null;
  guardian: OrderingGuardianFormValues | null;
}

export interface OrderingEligibilityRawFormValues {
  firstName: string;
  lastName: string;
  dob: string;
  sexAtBirth: string;
  email: string;
  phone: string;
  boneMarrowTransplant: YesNoBooleanFormValue;
  promoCode: string;
  guardian: OrderingGuardianFormValues;
}

export const getInitialValues = (promoCode: string = ''): OrderingEligibilityRawFormValues => ({
  firstName: '',
  lastName: '',
  dob: '',
  sexAtBirth: '',
  email: '',
  phone: '',
  boneMarrowTransplant: '',
  promoCode,
  guardian: {
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    relationship: '',
    legalAuthorityConfirmed: false,
  },
});

export type OrderingEligibilityFormikProps = FormikProps<OrderingEligibilityRawFormValues>;
export type OrderingEligibilityFormikHelpers = FormikHelpers<OrderingEligibilityRawFormValues>;

interface EligibilitySchemaOptions {
  allowsMinorWithGuardian: boolean;
}

export const orderingGuardianFormSchema = Yup.object({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  dob: AdultDOBValidator.required('Date of birth is required.'),
  email: EmailValidator.required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  relationship: Yup.string().trim().required('Relationship is required'),
  legalAuthorityConfirmed: Yup.boolean()
    .oneOf([true], 'You must confirm legal authority to continue')
    .required('You must confirm legal authority to continue'),
});

export const getEligibilityFormSchema = (
  params: EligibilitySchemaOptions,
): Yup.ObjectSchema<OrderingEligibilityValidatedFormValues> => {
  const allowMinor = params.allowsMinorWithGuardian;

  const dobValidator = allowMinor ? DOBValidator : AdultDOBValidator;

  const dobIsNotMinor = (dob: string | undefined) => {
    if (!dob || !allowMinor) {
      return true;
    }

    return isAdultDOB(dob);
  };

  return Yup.object({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    dob: dobValidator.required('Date of birth is required.'),
    sexAtBirth: Yup.string().oneOf(['MALE', 'FEMALE'], 'Please select an option').required('Sex at birth is required'),
    email: EmailValidator.when('dob', {
      is: dobIsNotMinor,
      then: schema => schema.required('Email is required'),
    }),
    phone: NullableStringValidator.when('dob', {
      is: dobIsNotMinor,
      then: schema => schema.required('Phone number is required'),
    }),
    boneMarrowTransplant: Yup.string()
      .oneOf(['YES', 'NO'], 'Please select an option')
      .required('This field is required'),
    promoCode: NullableStringValidator,
    guardian: orderingGuardianFormSchema.nullable().when('dob', {
      is: (dob: string | undefined) => !dobIsNotMinor(dob),
      then: schema =>
        schema
          .required('A guardian is required for patients under 18 years old.')
          .test('unique-email', 'Cannot be the same as the patient', (value, context) => {
            const guardianEmail = value?.email;
            const patientEmail: string = context.parent?.email || '';

            if (!guardianEmail || !patientEmail) {
              return true;
            }

            return guardianEmail.trim().toLowerCase() !== patientEmail.trim().toLowerCase();
          }),
      otherwise: schema => schema.transform(() => null),
    }),
  }).required() as Yup.ObjectSchema<OrderingEligibilityValidatedFormValues>;
};
